"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Globe, Check } from "lucide-react";
import {
  SUPPORTED_LANGUAGES,
  getCurrentLanguage,
  switchLanguage,
} from "./GoogleTranslateScript";

interface LanguageSelectorProps {
  variant?: "desktop" | "mobile";
  className?: string;
}

export default function LanguageSelector({
  variant = "desktop",
  className = "",
}: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(SUPPORTED_LANGUAGES[0]);
  const [mounted, setMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    setCurrentLang(getCurrentLanguage());
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (code: string) => {
    setIsOpen(false);
    switchLanguage(code);
  };

  if (!mounted) {
    return (
      <div className={`h-[38px] flex items-center gap-1.5 px-3.5 rounded-md border border-zinc-700 bg-zinc-900/60 text-zinc-300 text-xs font-semibold ${className}`}>
        <Globe className="w-3.5 h-3.5 text-orange-500 animate-pulse" />
        <span>EN</span>
      </div>
    );
  }

  if (variant === "mobile") {
    return (
      <div className={`w-full py-2 notranslate ${className}`} translate="no">
        <div className="flex items-center gap-2 mb-2 text-zinc-400 text-xs font-bold uppercase tracking-wider notranslate">
          <Globe className="w-4 h-4 text-orange-500" />
          <span>Language / Langue</span>
        </div>
        <div className="grid grid-cols-2 gap-2 notranslate">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isSelected =
              currentLang.code === lang.code ||
              (lang.code === "zh-CN" && currentLang.code === "zh");
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleSelect(lang.code)}
                className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-all duration-200 border notranslate ${
                  isSelected
                    ? "bg-orange-500/20 border-orange-500 text-orange-400 font-semibold"
                    : "bg-zinc-800/80 border-zinc-700 text-zinc-300 hover:bg-zinc-700/80 hover:border-zinc-600"
                }`}
              >
                <div className="flex flex-col text-left notranslate">
                  <span className="text-xs font-medium text-white">{lang.name}</span>
                  <span className="text-[10px] text-zinc-400">{lang.englishName}</span>
                </div>
                {isSelected ? (
                  <Check className="w-4 h-4 text-orange-500 shrink-0" />
                ) : (
                  <span className="text-[10px] uppercase font-bold text-zinc-500 notranslate">
                    {lang.shortCode}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Desktop Dropdown
  return (
    <div ref={dropdownRef} className={`relative inline-block text-left notranslate ${className}`} translate="no">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-[38px] flex items-center gap-2 px-3.5 rounded-md border border-zinc-700/80 bg-zinc-900/80 hover:bg-zinc-800 hover:border-orange-500/80 transition-all duration-200 text-zinc-200 font-semibold text-xs shadow-sm hover:shadow-orange-500/10 cursor-pointer focus:outline-none notranslate"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Globe className="w-3.5 h-3.5 text-orange-500" />
        <span className="font-bold tracking-wide text-zinc-100">{currentLang.shortCode}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-orange-500" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            translate="no"
            className="absolute right-0 mt-2 w-48 rounded-md bg-[#1E1E1E]/95 backdrop-blur-md border border-zinc-700/80 shadow-2xl z-[9999] overflow-hidden py-1.5 notranslate"
          >
            <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 border-b border-zinc-800 mb-1">
              Select Language
            </div>
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isSelected =
                currentLang.code === lang.code ||
                (lang.code === "zh-CN" && currentLang.code === "zh");
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors duration-150 text-left ${
                    isSelected
                      ? "bg-orange-500/15 text-orange-400 font-semibold"
                      : "text-zinc-300 hover:bg-zinc-800/80 hover:text-white"
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-xs leading-tight">
                      {lang.name}
                    </span>
                    <span className="text-[10px] text-zinc-500 font-normal">
                      {lang.englishName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        isSelected
                          ? "bg-orange-500 text-white"
                          : "bg-zinc-800 text-zinc-400"
                      }`}
                    >
                      {lang.shortCode}
                    </span>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-orange-500" />
                    )}
                  </div>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
